import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, Search, User, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/shop/hijabs", label: "Hijabs" },
  { to: "/shop/bags", label: "Bags" },
  { to: "/custom-bag", label: "Bespoke" },
  { to: "/about", label: "Atelier" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
    setMobile(false);
  };

  return (
    <header className="glass-header sticky top-0 z-50">
      {/* announcement */}
      <div className="bg-[#0A0A0A] text-white text-[0.65rem] tracking-[0.25em] uppercase py-2 text-center">
        Complimentary delivery across the UAE over AED 200 • Bespoke bag atelier now open
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-3 items-center py-5">
          {/* left: search desktop / hamburger mobile */}
          <div className="flex items-center gap-3">
            <button data-testid="nav-mobile-toggle" className="lg:hidden" onClick={() => setMobile(true)} aria-label="Menu">
              <Menu size={20} strokeWidth={1.4} />
            </button>
            <form onSubmit={submitSearch} className="hidden lg:flex items-center gap-2 border-b border-[#E5E5DF] pb-1 w-64">
              <Search size={16} strokeWidth={1.4} />
              <input
                data-testid="header-search-input"
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search the atelier"
                className="bg-transparent text-sm outline-none w-full placeholder:text-[#8A8A8A]"
              />
            </form>
          </div>

          {/* center: brand */}
          <Link to="/" data-testid="brand-logo" className="text-center">
            <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#5A5A5A]">Maison</div>
            <div className="serif text-3xl lg:text-4xl leading-none">Atelier Noir</div>
          </Link>

          {/* right: icons */}
          <div className="flex items-center gap-5 justify-end">
            <Link data-testid="nav-wishlist-link" to="/wishlist" className="hover:text-[#C5A059] transition-colors">
              <Heart size={20} strokeWidth={1.4} />
            </Link>
            <Link data-testid="nav-account-link" to={user ? "/account" : "/login"} className="hover:text-[#C5A059] transition-colors">
              <User size={20} strokeWidth={1.4} />
            </Link>
            <button data-testid="nav-cart-button" onClick={() => setOpen(true)} className="relative hover:text-[#C5A059] transition-colors">
              <ShoppingBag size={20} strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 text-[10px] bg-[#0A0A0A] text-white rounded-full w-4 h-4 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* desktop nav */}
        <nav className="hidden lg:flex items-center justify-center gap-10 pb-4 text-[0.7rem] tracking-[0.25em] uppercase">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}-link`}
              className={({ isActive }) => `transition-colors hover:text-[#C5A059] ${isActive ? "text-[#C5A059]" : "text-[#0A0A0A]"}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* mobile drawer */}
      {mobile && (
        <div className="fixed inset-0 bg-white z-[60] flex flex-col p-6 lg:hidden">
          <div className="flex justify-between items-center mb-10">
            <span className="serif text-2xl">Atelier Noir</span>
            <button data-testid="nav-mobile-close" onClick={() => setMobile(false)}><X size={22} /></button>
          </div>
          <form onSubmit={submitSearch} className="flex items-center gap-2 border-b border-[#E5E5DF] pb-2 mb-8">
            <Search size={16} /><input className="bg-transparent text-sm outline-none w-full" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
          </form>
          <nav className="flex flex-col gap-6 text-lg">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMobile(false)} className="serif">{n.label}</Link>
            ))}
            <Link to={user ? "/account" : "/login"} onClick={() => setMobile(false)} className="serif">{user ? "Account" : "Sign in"}</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
